import { NoonReportStatusEnum, NoonReportTypeEnum } from './AolEnums';
import { FuelRobData } from './FuelRobData';
import { ValidatedNoonReport } from './ValidatedNoonReport';
import { CargoTankTemperature } from './CargoTankTemperature';
import { Duration } from 'luxon';

export class PortLogAolItem {
    startTimeLocalTime: Date;
    stopTimeLocalTime: Date;
    startTimeUtc?: Date | null;
    stopTimeUtc?: Date | null;
    noonReportType: NoonReportTypeEnum;
    imosPortCallId: number;
    isValid: boolean;
    errorEvents: ErrorEvent[];
    constructor(
        startTimeLocalTime: Date,
        stopTimeLocalTime: Date,
        noonReportType: NoonReportTypeEnum,
        imosPortCallId: number,
        isValid: boolean,
        errorEvents: ErrorEvent[],
        startTimeUtc?: Date | null,
        stopTimeUtc?: Date | null
    ) {
        this.startTimeLocalTime = startTimeLocalTime;
        this.stopTimeLocalTime = stopTimeLocalTime;
        this.startTimeUtc = startTimeUtc;
        this.stopTimeUtc = stopTimeUtc;
        this.noonReportType = noonReportType;
        this.imosPortCallId = imosPortCallId;
        this.isValid = isValid;
        this.errorEvents = errorEvents;
    }
}

export type ErrorEvent = {
    eventId: string;
    eventTypeId: number;
    voyageNumber: string;
    portCallId: number;
    portName: string;
    berthName: string;
    eventDate: {
        actualDateTimeLocal: Date;
        actualDateTimeUTC: Date;
    };
};

export type AolValidationItem = {
    type: 'noonReportType' | 'numberOfHours' | 'imosPortCallId' | 'missingPortLogAolItem';
    noonReport: ValidatedNoonReport;
    data:
        | { noonReportPortCallId: number; portLogPortCallId: number }
        | { noonReportHours: number; portLogHours: number }
        | { noonReportType: NoonReportTypeEnum; portLogType: NoonReportTypeEnum }
        | { noonReportId: number };
};

export type PortVisit = {
    noonReportId: number;
    portName?: string | null;
};

export type AolGroup = {
    aolDate: string;
    aolHours: number;
    aolDataItems: AolDataItem[];
    status: NoonReportStatusEnum;
    isValid: boolean;
    aolVersion: number;
    hasTankersEventMismatch: boolean;
    isBeforeOrcaWebStartDate: boolean;
};

export type AolDataItemProperties = {
    isSurplusSteamUsedForFreshWaterProduction?: boolean;
    isDryDockingAolReport?: boolean;
    hasTankPreparationActivity?: boolean;
    hasTankPreparationAolData?: boolean;
    hasCargoHeating?: boolean;
    tankCleaningHours?: number;
    totalMainEngineConsumption?: number;
    consumptionOrderHours?: number;
    speedOrderHours?: number;
    nonTargetOrderHours?: number;
    shiftingAndMooringPortActivityHours?: number;
    totalHfoConsumption?: number;
    totalMgoConsumption?: number;
};

export type AolDataItem = {
    id: number;
    noonReport?: ValidatedNoonReport;
    portName?: string | null;
    aolItem?: AolItem;
    reportDate: Date;
    reportType: NoonReportTypeEnum;
    status: NoonReportStatusEnum;
    aolValidationItems?: AolValidationItem[] | null;
    reportHours: number;
    reportHoursWithoutDst: number;
    aolVersion: number;
    reportFromTimeLocalTime: Date | null;
    reportToTimeLocalTime: Date | null;
    reportFromTimeUtc: Date | null;
    reportToTimeUtc: Date | null;
    isValid: boolean;
    isReportAtNoon: boolean;
    indexInDay: number;
    numberOfReportsInDay: number;
    aolDataItemProperties: AolDataItemProperties;
    simpleModeAol: boolean;
    useNewConsumptionModel: boolean;
};

export class AolItem {
    portLogAolItem: PortLogAolItem;

    dryDockingTime: Duration;
    loadingTimes: Duration;
    unloadingTimes: Duration;
    shiftingMooringTime: Duration;
    idleTime: Duration;
    anchorageTime: Duration;
    steamingForTankCleaningTime: Duration;
    canalTransitTime: Duration;
    tankCleaningTime: Duration;

    operationalTargetSpeedTime: Duration;
    operationalTargetConsumptionTime: Duration;
    operationalNonTargetTime: Duration;
    operationalAwaitingOrdersOrDriftingTime: Duration;
    operationalTankPreparationTime: Duration;

    navigationalEcaZoneTime: Duration;
    navigationalCasualtyAndSarTime: Duration;
    navigationalHighRiskAreaTime: Duration;
    navigationalPilotManeuveringTime: Duration;
    navigationalRiverPortManeuveringTime: Duration;
    navigationalSafetyManeuveringTime: Duration;
    navigationalStormShelteringTime: Duration;
    navigationalTrafficManeuveringTime: Duration;
    navigationalCasualtyAndSarDrillTime: Duration;

    technicalAuxiliarySystemFailureTime: Duration;
    technicalFuelQualityIssueTime: Duration;
    technicalPropulsionSystemFailureTime: Duration;
    technicalOtherTechnicalIssueTime: Duration;
    portDescription: string;
    voyageNumber: string;
    lastPortVisits: PortVisit[];
    fuelRobDatas: FuelRobData[];
    cargoTankTemperatures: CargoTankTemperature[];
    cargoOnBoard: number;
    timeZoneChangeHours: number;
    dstOffsetHours: number;

    constructor(
        portLogAolItem: PortLogAolItem,
        dryDockingTime: Duration,
        loadingTimes: Duration,
        unloadingTimes: Duration,
        shiftingMooringTime: Duration,
        idleTime: Duration,
        anchorageTime: Duration,
        steamingForTankCleaningTime: Duration,
        canalTransitTime: Duration,
        tankCleaningTime: Duration,

        operationalTargetSpeedTime: Duration,
        operationalTargetConsumptionTime: Duration,
        operationalNonTargetTime: Duration,
        operationalAwaitingOrdersOrDriftingTime: Duration,
        operationalTankPreparationTime: Duration,

        navigationalEcaZoneTime: Duration,
        navigationalCasualtyAndSarTime: Duration,
        navigationalHighRiskAreaTime: Duration,
        navigationalPilotManeuveringTime: Duration,
        navigationalRiverPortManeuveringTime: Duration,
        navigationalSafetyManeuveringTime: Duration,
        navigationalStormShelteringTime: Duration,
        navigationalTrafficManeuveringTime: Duration,
        navigationalCasualtyAndSarDrillTime: Duration,

        technicalAuxiliarySystemFailureTime: Duration,
        technicalFuelQualityIssueTime: Duration,
        technicalPropulsionSystemFailureTime: Duration,
        technicalOtherTechnicalIssueTime: Duration,
        portDescription: string,
        voyageNumber: string,
        lastPortVisits: PortVisit[],
        fuelRobDatas: FuelRobData[],
        cargoTankTemperatures: CargoTankTemperature[],
        cargoOnBoard: number,
        timeZoneChangeHours: number,
        dstOffsetHours: number
    ) {
        this.portLogAolItem = portLogAolItem;
        this.dryDockingTime = dryDockingTime;
        this.loadingTimes = loadingTimes;
        this.unloadingTimes = unloadingTimes;
        this.shiftingMooringTime = shiftingMooringTime;
        this.anchorageTime = anchorageTime;
        this.idleTime = idleTime;
        this.steamingForTankCleaningTime = steamingForTankCleaningTime;
        this.canalTransitTime = canalTransitTime;
        this.tankCleaningTime = tankCleaningTime;

        this.operationalTargetConsumptionTime = operationalTargetConsumptionTime;
        this.operationalTargetSpeedTime = operationalTargetSpeedTime;
        this.operationalNonTargetTime = operationalNonTargetTime;
        this.operationalAwaitingOrdersOrDriftingTime = operationalAwaitingOrdersOrDriftingTime;
        this.operationalTankPreparationTime = operationalTankPreparationTime;

        this.navigationalEcaZoneTime = navigationalEcaZoneTime;
        this.navigationalCasualtyAndSarTime = navigationalCasualtyAndSarTime;
        this.navigationalHighRiskAreaTime = navigationalHighRiskAreaTime;
        this.navigationalPilotManeuveringTime = navigationalPilotManeuveringTime;
        this.navigationalRiverPortManeuveringTime = navigationalRiverPortManeuveringTime;
        this.navigationalSafetyManeuveringTime = navigationalSafetyManeuveringTime;
        this.navigationalStormShelteringTime = navigationalStormShelteringTime;
        this.navigationalTrafficManeuveringTime = navigationalTrafficManeuveringTime;
        this.navigationalCasualtyAndSarDrillTime = navigationalCasualtyAndSarDrillTime;

        this.technicalAuxiliarySystemFailureTime = technicalAuxiliarySystemFailureTime;
        this.technicalFuelQualityIssueTime = technicalFuelQualityIssueTime;
        this.technicalOtherTechnicalIssueTime = technicalOtherTechnicalIssueTime;
        this.technicalPropulsionSystemFailureTime = technicalPropulsionSystemFailureTime;

        this.portDescription = portDescription;
        this.voyageNumber = voyageNumber;
        this.lastPortVisits = lastPortVisits;
        this.fuelRobDatas = fuelRobDatas;
        this.cargoTankTemperatures = cargoTankTemperatures;
        this.cargoOnBoard = cargoOnBoard;
        this.timeZoneChangeHours = timeZoneChangeHours;
        this.dstOffsetHours = dstOffsetHours;
    }
}
